import { PageShell, Section, Callout } from '@/components/content/ContentBits';
import AccordionBlock from '@/components/AccordionBlock';
import { ArrowDown } from 'lucide-react';

const RECIPE_TABLE = [
  { need: '"Every funder letter/report takes forever and sounds different each time"', recipe: '1 · The Skill' },
  { need: '"We have data in sheets but leadership never sees it clearly"',            recipe: '2 · The Dashboard' },
  { need: '"The same manual sequence eats hours every week"',                        recipe: '3 · The Autopilot' },
  { need: "\"We have one important spreadsheet we don't fully understand\"",          recipe: '4 · The Analyst' },
  { need: '"We\'re new to all of this / needs are everywhere"',                      recipe: '5 · The Org Brain' },
  { need: '"Our comms must exist in Kannada/Hindi/Tamil too"',                       recipe: '1 · The Skill (vernacular variant)' },
];

const LADDER = [
  'The screen\'s "Stuck? Try this"',
  'Your own judgment',
  'The mentor beside you',
  'The stage — only between segments',
];

const RECIPE_NOTES = [
  {
    title: 'The Skill',
    body: "The examples ARE the build — push for real past outputs, not descriptions. Vague tone words (\"professional\") are the common wall; coach contrastive instructions (\"warm like X, never salesy like Y\"). Ask: \"what would you tell a new hire doing this?\"",
  },
  {
    title: 'The Dashboard',
    body: "The trap is chart-fiddling — hold them to \"the 3 questions leadership asks.\" Messy data? That IS the build: have Claude diagnose and clean first, narrating why. The verify-two-numbers step is non-negotiable.",
  },
  {
    title: 'The Autopilot',
    body: "Ruthlessly shrink scope — one trigger, one action, working. The error-paste loop is the teachable moment; don't fix it for them. Workspace Studio first if their org has it; Apps Script is the universal default.",
  },
  {
    title: 'The Analyst',
    body: "Steer from \"summarize\" to questions — insight comes from interrogation. Claude for Excel only for true Microsoft 365 orgs with work accounts; otherwise upload-to-chat, and don't burn minutes on installs.",
  },
  {
    title: 'The Org Brain',
    body: "Resist feature tours — depth of context beats breadth of features. The instructions file is the build; treat it like onboarding a new hire. This recipe cannot fail — which is exactly why it's the safety net.",
  },
];

const MentorsPage = () => (
  <PageShell
    eyebrow="For mentors"
    title="Mentor Resources"
    subtitle="Everything you need to coach a table through the AI Lab — the one rule, the escalation ladder, and per-recipe notes."
    testId="mentors-page"
  >
    {/* The one rule */}
    <Section number={1} title="The one rule">
      <Callout label="The one rule" variant="teal">
        <p className="text-lg font-semibold mb-2">Coach, don't drive.</p>
        <p>
          Anyone in this room can build what these organizations need in ten minutes. The harder game —
          the one you signed up for — is getting someone to build it themselves in forty. They type.
          They decide. You ask the questions. What's obvious to you is brand-new here: "just add more
          context" can be a revelation. The win isn't impressing anyone — it's them leaving feeling
          capable. The tool they build is the artifact; the confidence they leave with is the gift.
        </p>
      </Callout>
    </Section>

    {/* Escalation ladder */}
    <Section number={2} title="Your escalation ladder">
      <p className="text-ink-secondary leading-relaxed mb-6">
        There are no event floaters today. The support model:
      </p>
      <div className="flex flex-col gap-2" data-testid="escalation-ladder">
        {LADDER.map((rung, i) => (
          <div key={i} className="flex flex-col items-start gap-2">
            <div className="flex items-center gap-3 rounded-2xl bg-white border border-black/[0.07] shadow-sm px-5 py-4 w-full">
              <span className="w-7 h-7 rounded-full bg-brand-teal text-white font-bold text-sm flex items-center justify-center shrink-0">{i + 1}</span>
              <span className="font-semibold text-ink">{rung}</span>
            </div>
            {i < LADDER.length - 1 && (
              <ArrowDown size={16} className="text-ink-muted ml-4" strokeWidth={2.2} />
            )}
          </div>
        ))}
      </div>
      <p className="text-ink-secondary leading-relaxed mt-6">
        If the table next to you is drowning, help them — thirty of you is more support than any event
        staff could be.
      </p>
    </Section>

    {/* Prompt School: your role */}
    <Section number={3} title="Prompt School: your role">
      <Callout label="Your job here" variant="orange">
        <p>
          In the policy exercise you coach <strong>scaffold completeness</strong> and{' '}
          <strong>critique quality</strong> — never legal content (jurisdiction hints are on the screen).
          If they skip a scaffold field, ask why; don't fill it. In the critique, your best question is{' '}
          <em>"what would embarrass you if leadership read this?"</em>
        </p>
      </Callout>
    </Section>

    {/* Recipe Picker */}
    <Section number={4} title="The Recipe Picker">
      <p className="text-ink-secondary mb-5">Same table the teams see in Session 6 — use it to point a stuck table at the right build.</p>
      <div className="rounded-2xl bg-white border border-black/[0.07] shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-ink-muted border-b border-black/[0.06]">
              <th className="px-5 py-3 font-semibold">If their need sounds like…</th>
              <th className="px-5 py-3 font-semibold">Point them to</th>
            </tr>
          </thead>
          <tbody>
            {RECIPE_TABLE.map((row, i) => (
              <tr key={i} className="border-t border-black/[0.05] first:border-0">
                <td className="px-5 py-3 text-ink">{row.need}</td>
                <td className="px-5 py-3 font-semibold text-brand-teal whitespace-nowrap">{row.recipe}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>

    {/* Per-recipe coaching notes */}
    <Section number={5} title="Per-recipe coaching notes">
      <div className="space-y-3" data-testid="recipe-coaching-notes">
        {RECIPE_NOTES.map((r) => (
          <AccordionBlock key={r.title} title={r.title}>
            <p className="text-sm text-ink-secondary leading-relaxed pt-1">{r.body}</p>
          </AccordionBlock>
        ))}
      </div>
    </Section>

    {/* The 1:15 reversion */}
    <Section number={6} title="The 1:15 reversion">
      <Callout label="The 15-second demo test" variant="teal">
        <p>
          When the stage calls the pulse check, apply the test at your table honestly: can your org demo
          a core of the build in fifteen seconds, right now? If not, switch to the{' '}
          <strong>Org Brain</strong> — fifteen minutes, guaranteed real thing, and everything started
          stays saved in the workspace. Say it the way it's meant: <em>"Let's bank a win."</em> Never
          framed as failure.
        </p>
      </Callout>
    </Section>
  </PageShell>
);

export default MentorsPage;
